import { type FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import cx from "classnames";
import { useUnit } from "effector-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Controller, Zoom } from "swiper/modules";
import type { Swiper as SwiperClass } from "swiper";
import type { Photo, Video } from "../../types";
import { buildLargestResponsiveUrl } from "../../utils/responsiveImage";
import { markImageLoaded, markImageError } from "../../utils/markImageSettled";
import CloseIcon from "../../icons/close.svg?react";
import LeftIcon from "../../icons/left.svg?react";
import RightIcon from "../../icons/right.svg?react";
import { useI18n } from "../../i18n";
import { $locale } from "../../state/locale";
import { LOCALE_TEXT_DIRECTION } from "../../shared/locale";
import { PhotoViewerCaption } from "./PhotoViewerCaption";
import { PhotoViewerDesktopCaption } from "./PhotoViewerDesktopCaption";
import { PhotoViewerVideoSlide } from "./PhotoViewerVideoSlide";
import { useMobilePreviewContainer } from "../../context/MobilePreviewContext";
import { useLayoutMode } from "../../hooks/useLayoutMode";

import "swiper/css";
import "swiper/css/zoom";
import styles from "./PhotoViewer.module.css";

type Props = {
  items: Array<Photo | Video>;
  open: boolean;
  index?: number;
  onClose: () => void;
  onIndexChange?: (index: number) => void;
};

const getThumbnailSrc = (item: Photo | Video): string =>
  item.type === "video"
    ? (item.thumbnailSrc ?? item.src)
    : (item.thumbnailSrc ?? item.src);

export const PhotoViewer: FC<Props> = ({
  items,
  open,
  index,
  onClose,
  onIndexChange,
}) => {
  const i18n = useI18n();
  const locale = useUnit($locale);
  const isRtl = LOCALE_TEXT_DIRECTION[locale] === "rtl";
  const previewContainer = useMobilePreviewContainer();
  const layoutMode = useLayoutMode();
  const isDesktop = layoutMode === "desktop";
  const swiperRef = useRef<SwiperClass | null>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(index ?? 0);
  const [footerHeight, setFooterHeight] = useState(0);
  const [captionSwiper, setCaptionSwiper] = useState<SwiperClass | null>(null);
  const [captionExpanded, setCaptionExpanded] = useState(false);
  const [captionCanExpand, setCaptionCanExpand] = useState(false);

  const isSingle = items.length <= 1;
  const isMultiple = items.length > 1;
  const activeItem = items[activeIndex];

  useEffect(() => {
    if (index != null && swiperRef.current && swiperRef.current.realIndex !== index) {
      if (isSingle) {
        swiperRef.current.slideTo(index);
      } else {
        swiperRef.current.slideToLoop(index);
      }
    }
  }, [index, isSingle]);

  // Collapse caption whenever the active slide changes — the new
  // photo's description is likely different and starting collapsed is
  // less surprising than carrying expand state across slides.
  useEffect(() => {
    setCaptionExpanded(false);
  }, [activeIndex]);

  const handleSlideChange = useCallback(
    (swiper: SwiperClass) => {
      const realIndex = swiper.realIndex;
      setActiveIndex(realIndex);
      onIndexChange?.(realIndex);
    },
    [onIndexChange],
  );

  const handleSwiper = useCallback((swiper: SwiperClass) => {
    swiperRef.current = swiper;
  }, []);

  // Mobile-only: track footer height so the swiper area leaves room
  // for the caption + nav. Desktop layout uses flexbox and doesn't need
  // a measured offset.
  useLayoutEffect(() => {
    if (isDesktop) {
      setFooterHeight(0);
      return;
    }
    const el = footerRef.current;
    if (!el) {
      setFooterHeight(0);
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      setFooterHeight(entry.borderBoxSize[0]?.blockSize ?? el.getBoundingClientRect().height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [open, isDesktop]);

  const handlePrev = useCallback(() => {
    swiperRef.current?.slidePrev();
  }, []);

  const handleNext = useCallback(() => {
    swiperRef.current?.slideNext();
  }, []);

  const handleThumbClick = useCallback(
    (idx: number) => {
      const swiper = swiperRef.current;
      if (!swiper) return;
      if (isSingle) {
        swiper.slideTo(idx);
      } else {
        swiper.slideToLoop(idx);
      }
    },
    [isSingle],
  );

  // Desktop thumbnails — fade hint at top/bottom of the column when
  // it overflows, so the user can see there's more to scroll.
  // Mirrors the caption's scroll-mask pattern (classList toggles, no
  // React state — keeps scroll smooth even with many thumbs).
  const thumbsRef = useRef<HTMLDivElement>(null);
  const updateThumbsMask = useCallback(() => {
    const el = thumbsRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const overflowing = scrollHeight > clientHeight + 1;
    const atTop = scrollTop <= 2;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 2;
    el.classList.toggle(styles.thumbsMaskTop, overflowing && !atTop && atBottom);
    el.classList.toggle(styles.thumbsMaskBottom, overflowing && atTop && !atBottom);
    el.classList.toggle(styles.thumbsMaskBoth, overflowing && !atTop && !atBottom);
  }, []);

  useLayoutEffect(() => {
    updateThumbsMask();
  }, [items.length, isDesktop, updateThumbsMask]);

  // Keep the active thumb in view as the user moves between photos
  // via prev/next arrows or photo swipe — without this the column
  // can sit on the early thumbs while the active one is scrolled off
  // the bottom. `block: 'nearest'` skips the scroll when the thumb
  // is already visible.
  useEffect(() => {
    if (!isDesktop || !isMultiple) return;
    const container = thumbsRef.current;
    if (!container) return;
    const activeThumb = container.children[activeIndex];
    if (!(activeThumb instanceof HTMLElement)) return;
    activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeIndex, isDesktop, isMultiple]);

  const handleCaptionToggle = useCallback(() => {
    setCaptionExpanded((v) => !v);
  }, []);

  // Stable identity — needed because PhotoViewerDesktopCaption fires
  // `onCanExpandChange` on every measure and a fresh inline arrow would
  // trigger an effect storm.
  const handleCanExpandChange = useCallback((canExpand: boolean) => {
    setCaptionCanExpand(canExpand);
  }, []);

  // Title/description gating — desktop caption renders nothing when
  // both are absent, but we also need to suppress the chevron + the
  // empty caption slot in the layout column.
  const hasCaptionContent = useMemo(() => {
    if (!activeItem) return false;
    const title = activeItem.title ?? activeItem.caption;
    const description = activeItem.title ? activeItem.description : undefined;
    return Boolean(title || description);
  }, [activeItem]);

  if (!open) return null;

  const portalTarget = previewContainer ?? document.body;

  const renderSwiperSlides = () =>
    items.map((item) => (
      <SwiperSlide key={item.id} zoom={item.type === "photo"}>
        {item.type === "video" ? (
          <PhotoViewerVideoSlide item={item} />
        ) : (
          <img
            src={buildLargestResponsiveUrl(item.src, item.width) ?? item.src}
            alt={item.caption ?? item.title ?? ""}
            className={styles.slideImage}
            draggable={false}
            onLoad={markImageLoaded}
            onError={markImageError}
          />
        )}
      </SwiperSlide>
    ));

  if (isDesktop) {
    return createPortal(
      <div className={cx(styles.overlay, styles.overlayDesktop)} dir="ltr">
        {isMultiple && (
          <div
            ref={thumbsRef}
            className={styles.thumbs}
            onScroll={updateThumbsMask}
          >
            {items.map((item, idx) => (
              <button
                key={item.id}
                type="button"
                className={cx(styles.thumb, idx === activeIndex && styles.thumbActive)}
                onClick={() => handleThumbClick(idx)}
              >
                <img
                  src={getThumbnailSrc(item)}
                  alt={item.caption ?? item.title ?? ""}
                  className={styles.thumbImage}
                  draggable={false}
                  onLoad={markImageLoaded}
                  onError={markImageError}
                />
              </button>
            ))}
          </div>
        )}

        <div className={styles.desktopMain}>
          <div className={styles.desktopPhoto}>
            <Swiper
              key={`desktop-${layoutMode}`}
              modules={[Zoom, Controller]}
              onSwiper={handleSwiper}
              onSlideChange={handleSlideChange}
              initialSlide={activeIndex}
              zoom={{ maxRatio: 3, toggle: true }}
              slidesPerView={1}
              spaceBetween={12}
              loop={isMultiple}
              grabCursor
              className={styles.swiper}
            >
              {renderSwiperSlides()}
            </Swiper>
          </div>

          {/* Arrows positioned at 40% of the main column (stable viewport
              fraction) rather than inside the photo container, whose
              height fluctuates with caption length and would make arrows
              visibly jump as the user navigates between photos. */}
          {isMultiple && (
            <>
              <button
                type="button"
                className={cx(styles.desktopNav, styles.desktopNavPrev)}
                onClick={handlePrev}
                aria-label={i18n.photoViewer.prev}
              >
                <LeftIcon />
              </button>
              <button
                type="button"
                className={cx(styles.desktopNav, styles.desktopNavNext)}
                onClick={handleNext}
                aria-label={i18n.photoViewer.next}
              >
                <RightIcon />
              </button>
            </>
          )}

          {hasCaptionContent && activeItem && (
            <div className={styles.desktopCaptionSlot} dir={isRtl ? "rtl" : "ltr"}>
              {captionCanExpand && (
                <div className={styles.expandWrapper}>
                  <button
                    type="button"
                    className={cx(
                      styles.expandButton,
                      captionExpanded && styles.expandButtonExpanded,
                    )}
                    onClick={handleCaptionToggle}
                    aria-label={
                      captionExpanded
                        ? i18n.photoViewer.collapse
                        : i18n.photoViewer.expand
                    }
                    aria-expanded={captionExpanded}
                  >
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden>
                      <path
                        d="M4 9.5L8 5.5L12 9.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              )}
              <PhotoViewerDesktopCaption
                item={activeItem}
                expanded={captionExpanded}
                onCanExpandChange={handleCanExpandChange}
              />
            </div>
          )}
        </div>

        <button
          className={styles.desktopClose}
          onClick={onClose}
          onPointerDown={(e) => e.preventDefault()}
          type="button"
          aria-label={i18n.photoViewer.close}
        >
          <CloseIcon />
        </button>
      </div>,
      portalTarget,
    );
  }

  // Mobile layout — unchanged from the original implementation.
  const captionOffset =
    footerHeight > 0
      ? `calc(var(--safe-bottom, 0px) + ${footerHeight + 16}px)`
      : "0px";

  const content = (
    <div className={styles.overlay} dir="ltr">
      <button
        className={cx(styles.closeButton, isRtl && styles.closeButtonRtl)}
        onClick={onClose}
        onPointerDown={(e) => e.preventDefault()}
        type="button"
      >
        <CloseIcon />
      </button>

      {isMultiple && (
        <div className={styles.counter} dir={isRtl ? "rtl" : "ltr"}>
          {activeIndex + 1} {i18n.photoViewer.counterSeparator} {items.length}
        </div>
      )}

      <div
        className={styles.swiperArea}
        style={{ paddingBottom: captionOffset }}
      >
        <Swiper
          key={`mobile-${layoutMode}`}
          modules={[Zoom, Controller]}
          onSwiper={handleSwiper}
          onSlideChange={handleSlideChange}
          controller={{ control: captionSwiper ?? undefined }}
          initialSlide={index ?? 0}
          zoom={{ maxRatio: 3, toggle: true }}
          slidesPerView={1}
          spaceBetween={12}
          loop={isMultiple}
          grabCursor
          className={styles.swiper}
        >
          {renderSwiperSlides()}
        </Swiper>
      </div>

      <div ref={footerRef} className={styles.footer}>
        <Swiper
          modules={[Controller]}
          onSwiper={setCaptionSwiper}
          initialSlide={index ?? 0}
          slidesPerView={1}
          spaceBetween={8}
          loop={isMultiple}
          allowTouchMove={false}
          autoHeight
          className={styles.captionSwiper}
        >
          {items.map((item) => (
            <SwiperSlide key={item.id}>
              <div dir={isRtl ? "rtl" : "ltr"}>
                <PhotoViewerCaption item={item} />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        {isMultiple && (
          <div className={styles.navButtons}>
            <button
              type="button"
              className={styles.navButton}
              onClick={handlePrev}
            >
              <LeftIcon />
            </button>
            <button
              type="button"
              className={styles.navButton}
              onClick={handleNext}
            >
              <RightIcon />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, portalTarget);
};
